import * as Sentry from '@sentry/react';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supportApi } from '@/lib/api';
import { SupportTicket, SupportMessage } from '@/types';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    LifeBuoy,
    Plus,
    MessageSquare,
    Clock,
    CheckCircle2,
    AlertCircle,
    Send,
    ImageIcon,
    Loader2,
    ChevronRight,
    Search,
    Filter,
    User as UserIcon,
    Building2,
    ArrowLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';

const formatDateSafe = (date: any, formatStr: string) => {
    try {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        return format(d, formatStr, { locale: es });
    } catch (e) {
        return '';
    }
};

export default function SupportPage() {
    const { user, gym, hasRole } = useAuth();
    const isSuperAdmin = hasRole(['super_admin']);

    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
    const [messages, setMessages] = useState<SupportMessage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isNewTicketModalOpen, setIsNewTicketModalOpen] = useState(false);
    const [showMobileChat, setShowMobileChat] = useState(false);

    // New Ticket Form State
    const [newTicket, setNewTicket] = useState({
        subject: '',
        category: 'support' as SupportTicket['category'],
        priority: 'medium' as SupportTicket['priority'],
        initialMessage: ''
    });

    // Chat State
    const [newMessage, setNewMessage] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadTickets();
    }, [gym?.gym_id, isSuperAdmin]);

    // Safety hatch for loading state
    useEffect(() => {
        if (isLoading) {
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    useEffect(() => {
        if (selectedTicket) {
            loadMessages(selectedTicket.id);
            const channel = subscribeToMessages(selectedTicket.id);
            return () => {
                if (channel) supabase.removeChannel(channel);
            };
        }
    }, [selectedTicket?.id]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadTickets = async () => {
        try {
            const { tickets } = await supportApi.listTickets(isSuperAdmin ? undefined : gym?.gym_id);
            setTickets(tickets);
        } catch (error) {
            console.error('Error loading tickets:', error);
            Sentry.captureException(error, { tags: { operation: 'support_load_tickets' } });
            toast.error('Error al cargar tickets');
        } finally {
            setIsLoading(false);
        }
    };

    const loadMessages = async (ticketId: string) => {
        try {
            const { messages } = await supportApi.getTicketMessages(ticketId);
            setMessages(messages);
        } catch (error) {
            console.error('Error loading messages:', error);
            Sentry.captureException(error, { tags: { operation: 'support_load_messages' } });
        }
    };

    const subscribeToMessages = (ticketId: string) => {
        const channel = supabase
            .channel(`ticket_messages:${ticketId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'support_messages',
                    filter: `ticket_id=eq.${ticketId}`
                },
                (payload) => {
                    const newMsg = payload.new as SupportMessage;
                    setMessages(prev => {
                        if (prev.find(m => m.id === newMsg.id)) return prev;
                        return [...prev, newMsg];
                    });
                }
            )
            .subscribe();

        return channel;
    };

    const handleCreateTicket = async () => {
        if (!newTicket.subject || !newTicket.initialMessage) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        setIsCreating(true);
        try {
            const { ticket } = await supportApi.createTicket({
                gym_id: gym?.gym_id || '',
                user_id: user?.user_id || '',
                subject: newTicket.subject,
                category: newTicket.category,
                priority: newTicket.priority,
                status: 'open'
            });

            if (ticket) {
                await supportApi.addMessage({
                    ticket_id: ticket.id,
                    user_id: user?.user_id || '',
                    content: newTicket.initialMessage,
                    is_admin_reply: false
                });

                toast.success('Ticket creado correctamente');
                setIsNewTicketModalOpen(false);
                setNewTicket({ subject: '', category: 'support', priority: 'medium', initialMessage: '' });
                loadTickets();
            }
        } catch (error) {
            console.error('Error creating ticket:', error);
            Sentry.captureException(error, { tags: { operation: 'support_create_ticket' } });
            toast.error('Error al crear el ticket');
        } finally {
            setIsCreating(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedTicket) return;

        setIsSending(true);
        try {
            await supportApi.addMessage({
                ticket_id: selectedTicket.id,
                user_id: user?.user_id || '',
                content: newMessage,
                is_admin_reply: isSuperAdmin
            });
            setNewMessage('');
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'support_send_message' } });
            toast.error('Error al enviar mensaje');
        } finally {
            setIsSending(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !selectedTicket) return;

        setUploadingImage(true);
        try {
            const path = `tickets/${selectedTicket.id}`;
            const { url } = await supportApi.uploadAttachment(file, path);

            await supportApi.addMessage({
                ticket_id: selectedTicket.id,
                user_id: user?.user_id || '',
                content: 'Imagen adjunta',
                attachment_url: url,
                is_admin_reply: isSuperAdmin
            });

            toast.success('Imagen enviada');
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'support_upload_file' } });
            toast.error('Error al subir imagen');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleStatusUpdate = async (newStatus: SupportTicket['status']) => {
        if (!selectedTicket) return;
        try {
            await supportApi.updateTicketStatus(selectedTicket.id, newStatus);
            setSelectedTicket(prev => prev ? { ...prev, status: newStatus } : null);
            setTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, status: newStatus } : t));
            toast.success(`Ticket ${newStatus === 'closed' ? 'cerrado' : 'actualizado'}`);
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'support_update_status' } });
            toast.error('Error al actualizar estado');
        }
    };

    const getStatusIcon = (status: SupportTicket['status']) => {
        switch (status) {
            case 'open': return <AlertCircle className="h-4 w-4 text-orange-500" />;
            case 'in_progress': return <Clock className="h-4 w-4 text-blue-500" />;
            case 'closed': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
        }
    };

    const getPriorityColor = (priority: SupportTicket['priority']) => {
        switch (priority) {
            case 'high': return 'bg-red-500/10 text-red-500';
            case 'medium': return 'bg-orange-500/10 text-orange-500';
            case 'low': return 'bg-slate-500/10 text-slate-500';
        }
    };

    return (
        <DashboardLayout
            title="Soporte Técnico"
            subtitle="¿En qué podemos ayudarte hoy?"
            actions={
                !isSuperAdmin && (
                    <Button onClick={() => setIsNewTicketModalOpen(true)} className="gradient-primary font-bold rounded-xl shadow-lg shadow-primary/20">
                        <Plus className="h-4 w-4 mr-2" />
                        NUEVO TICKET
                    </Button>
                )
            }
        >
            <div className="flex h-[calc(100dvh-6.5rem)] md:h-[calc(100vh-12rem)] bg-slate-50 dark:bg-black/20 backdrop-blur-xl rounded-2xl md:rounded-[2.5rem] border border-slate-200/50 dark:border-white/10 overflow-hidden relative shadow-2xl">

                {/* Tickets List */}
                <div className={cn(
                    "w-full md:w-[380px] flex flex-col border-r border-slate-200/50 dark:border-white/10 bg-white/30 dark:bg-white/5 shrink-0 h-full",
                    showMobileChat ? "hidden md:flex" : "flex"
                )}>

                    <div className="p-6 border-b border-slate-200/50 dark:border-white/10">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500 mr-1" />
                            <Input placeholder="Buscar tickets..." className="pl-10 h-11 bg-white/50 dark:bg-white/10 border-none rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500" />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center h-40 gap-3">
                                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                                <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Cargando tickets...</p>
                            </div>
                        ) : tickets.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full p-6 text-center text-muted-foreground">
                                <LifeBuoy className="h-12 w-12 mb-4 opacity-20" />
                                <p className="text-sm font-medium">No hay tickets registrados aún.</p>
                            </div>
                        ) : (
                            tickets.map(ticket => (
                                <button
                                    key={ticket.id}
                                    onClick={() => {
                                        setSelectedTicket(ticket);
                                        setShowMobileChat(true);
                                    }}
                                    className={cn(
                                        "w-full text-left p-4 rounded-2xl transition-all group relative",
                                        selectedTicket?.id === ticket.id
                                            ? "bg-white dark:bg-slate-900 shadow-xl border border-primary/20 scale-[1.02]"
                                            : "hover:bg-white/40 dark:hover:bg-white/5"
                                    )}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <span className={cn(
                                            "text-[10px] font-black uppercase px-2 py-0.5 rounded-full",
                                            getPriorityColor(ticket.priority)
                                        )}>
                                            {ticket.priority === 'high' ? 'Crítico' : ticket.priority === 'medium' ? 'Normal' : 'Bajo'}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground font-medium">
                                            {formatDateSafe(ticket.updated_at, 'HH:mm')}
                                        </span>
                                    </div>
                                    <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 truncate mb-1">
                                        {ticket.subject}
                                    </h4>
                                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-medium">
                                        {getStatusIcon(ticket.status)}
                                        <span className="uppercase tracking-wider">
                                            {ticket.status === 'open' ? 'Abierto' : ticket.status === 'in_progress' ? 'En pausa' : 'Finalizado'}
                                        </span>
                                    </div>
                                    {isSuperAdmin && ticket.gym_name && (
                                        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-white/5 flex items-center gap-2">
                                            <Building2 className="h-3 w-3 text-primary opacity-50" />
                                            <span className="text-[10px] font-bold uppercase text-primary/70 truncate">{ticket.gym_name}</span>
                                        </div>
                                    )}
                                    <ChevronRight className={cn(
                                        "absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary opacity-0 group-hover:opacity-100 transition-all",
                                        selectedTicket?.id === ticket.id && "opacity-100 translate-x-1"
                                    )} />
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                <div className={cn(
                    "flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-950 transition-all duration-300 h-full",
                    !showMobileChat && "hidden md:flex"
                )}>
                    {selectedTicket ? (
                        <>
                            {/* Chat Header */}
                            <div className="p-6 border-b border-slate-200/50 dark:border-white/10 flex items-center justify-between bg-white/40 dark:bg-white/5">
                                <div className="flex items-center gap-4">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="md:hidden text-slate-600 dark:text-slate-400"
                                        onClick={() => setShowMobileChat(false)}
                                    >
                                        <ArrowLeft className="h-5 w-5" />
                                    </Button>
                                    <div>
                                        <h3 className="font-black text-xl text-slate-900 dark:text-white truncate">{selectedTicket.subject}</h3>
                                        <div className="flex items-center gap-3 mt-1">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                                                ID: {selectedTicket.id.slice(0, 8)}
                                            </span>
                                            <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-white/20" />
                                            <span className="text-[10px] font-black uppercase tracking-widest text-primary">
                                                {selectedTicket.category.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {isSuperAdmin && (
                                    <div className="flex items-center gap-2">
                                        <Select
                                            value={selectedTicket.status}
                                            onValueChange={(val: any) => handleStatusUpdate(val)}
                                        >
                                            <SelectTrigger className="w-40 h-10 rounded-xl font-bold text-xs bg-white dark:bg-white/10 border-none shadow-sm text-slate-900 dark:text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-xl border-none shadow-2xl glass">
                                                <SelectItem value="open">🟢 Abierto</SelectItem>
                                                <SelectItem value="in_progress">🟡 En pausa</SelectItem>
                                                <SelectItem value="closed">🔴 Cerrar Caso</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            {/* Messages Container */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-primary/10 scrollbar-track-transparent">
                                {messages.map((msg, idx) => {
                                    const isMe = msg.user_id === user?.user_id;
                                    const showHeader = idx === 0 || messages[idx - 1].user_id !== msg.user_id;

                                    return (
                                        <div key={msg.id} className={cn(
                                            "flex flex-col max-w-[85%]",
                                            isMe ? "ml-auto items-end" : "items-start"
                                        )}>
                                            {showHeader && (
                                                <div className="flex items-center gap-2 mb-2 px-2">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                                        {msg.is_admin_reply ? '🛡️ SOPORTE GYMPRO' : msg.user_name || 'Usuario'}
                                                    </span>
                                                    <span className="text-[8px] font-black text-slate-300">
                                                        {formatDateSafe(msg.created_at, 'PP p')}
                                                    </span>
                                                </div>
                                            )}

                                            <div className={cn(
                                                "p-4 rounded-[2rem] shadow-sm relative group",
                                                isMe
                                                    ? "bg-primary text-white rounded-tr-none"
                                                    : "bg-slate-100 dark:bg-white/10 text-slate-900 dark:text-slate-100 rounded-tl-none border border-slate-200 dark:border-white/5"
                                            )}>
                                                {msg.attachment_url && (
                                                    <div className="mb-3 rounded-2xl overflow-hidden shadow-inner bg-black/5 dark:bg-white/5 p-1 max-w-sm">
                                                        <img
                                                            src={msg.attachment_url}
                                                            alt="Adjunto"
                                                            className="w-full h-auto object-contain cursor-pointer transition-transform hover:scale-105"
                                                            onClick={() => window.open(msg.attachment_url, '_blank')}
                                                        />
                                                    </div>
                                                )}
                                                <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap">
                                                    {msg.content}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Message Input */}
                            <div className="p-4 md:p-6 bg-white/50 dark:bg-white/5 border-t border-slate-200/50 dark:border-white/10">
                                {selectedTicket.status === 'closed' ? (
                                    <div className="text-center py-4 bg-red-500/10 border border-red-500/20 rounded-[2rem]">
                                        <p className="text-sm font-black text-red-500 uppercase tracking-widest">
                                            ESTE TICKET ESTÁ CERRADO. NO SE PUEDEN ENVIAR MÁS MENSAJES.
                                        </p>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="mt-2 font-bold text-xs"
                                            onClick={() => handleStatusUpdate('open')}
                                        >
                                            REABRIR CASO
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-end gap-3 max-w-4xl mx-auto">
                                        <div className="flex-1 relative flex items-center">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="absolute left-2 h-9 w-9 rounded-full hover:bg-primary/10 transition-colors"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploadingImage}
                                            >
                                                {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-5 w-5 text-primary" />}
                                            </Button>
                                            <input
                                                type="file"
                                                hidden
                                                ref={fileInputRef}
                                                onChange={handleFileUpload}
                                                accept="image/*"
                                            />
                                            <Textarea
                                                placeholder="Escribe tu respuesta aquí..."
                                                className="min-h-[50px] max-h-[150px] pl-14 pr-14 py-3 bg-white dark:bg-white/10 border-none rounded-[1.5rem] resize-none focus-visible:ring-primary shadow-inner text-slate-900 dark:text-white"
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onFocus={() => {
                                                    // Estabilizador de scroll al enfocar en móvil
                                                    setTimeout(() => scrollToBottom(), 300);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                            />
                                            <Button
                                                size="icon"
                                                className="absolute right-2 h-10 w-10 rounded-full gradient-primary shadow-lg"
                                                onClick={handleSendMessage}
                                                disabled={isSending || !newMessage.trim()}
                                            >
                                                {isSending ? <Loader2 className="h-4 w-4 animate-spin text-white" /> : <Send className="h-4 w-4 text-white" />}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 text-center bg-white dark:bg-slate-950">
                            <div className="h-16 w-16 lg:h-24 lg:w-24 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                                <MessageSquare className="h-8 w-8 lg:h-10 lg:w-10 text-primary" />
                            </div>
                            <h3 className="text-xl lg:text-2xl font-black text-slate-900 dark:text-white mb-2">Selecciona un ticket</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs lg:max-w-md mx-auto leading-relaxed">
                                Haz clic en una conversación para ver el historial o pulsa "Nuevo Ticket".
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* New Ticket Modal */}
            <Dialog open={isNewTicketModalOpen} onOpenChange={setIsNewTicketModalOpen}>
                <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-none shadow-2xl glass rounded-[2.5rem]">
                    <DialogHeader className="p-8 pb-0 text-left">
                        <DialogTitle className="text-2xl font-black uppercase tracking-tight">Nuevo Ticket de Soporte</DialogTitle>
                        <DialogDescription className="font-medium text-slate-500">
                            Describe tu problema para que podamos ayudarte lo antes posible.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="p-8 space-y-6">
                        <div className="space-y-4">
                            <div className="grid gap-2">
                                <Label htmlFor="subject" className="text-xs font-black uppercase tracking-widest ml-1">Asunto del ticket</Label>
                                <Input
                                    id="subject"
                                    placeholder="Ej: Problema con escáner QR"
                                    className="h-12 bg-white/50 dark:bg-white/5 border-none rounded-xl"
                                    value={newTicket.subject}
                                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label className="text-xs font-black uppercase tracking-widest ml-1">Categoría</Label>
                                    <Select
                                        value={newTicket.category}
                                        onValueChange={(val: any) => setNewTicket({ ...newTicket, category: val })}
                                    >
                                        <SelectTrigger className="h-12 bg-white/50 dark:bg-white/5 border-none rounded-xl">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl border-none shadow-2xl glass">
                                            <SelectItem value="support">🛠️ Soporte Técnico</SelectItem>
                                            <SelectItem value="billing">💰 Facturación</SelectItem>
                                            <SelectItem value="inquiry">❓ Consulta General</SelectItem>
                                            <SelectItem value="feature_request">💡 Sugerencia</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-xs font-black uppercase tracking-widest ml-1">Prioridad</Label>
                                    <Select
                                        value={newTicket.priority}
                                        onValueChange={(val: any) => setNewTicket({ ...newTicket, priority: val })}
                                    >
                                        <SelectTrigger className="h-12 bg-white/50 dark:bg-white/5 border-none rounded-xl">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl border-none shadow-2xl glass">
                                            <SelectItem value="low">☁️ Baja</SelectItem>
                                            <SelectItem value="medium">⚡ Media</SelectItem>
                                            <SelectItem value="high">🔥 Crítica</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label className="text-xs font-black uppercase tracking-widest ml-1">Mensaje inicial</Label>
                                <Textarea
                                    placeholder="Explica tu situación con detalle..."
                                    className="min-h-[120px] bg-white/50 dark:bg-white/5 border-none rounded-2xl resize-none"
                                    value={newTicket.initialMessage}
                                    onChange={(e) => setNewTicket({ ...newTicket, initialMessage: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="p-8 pt-0">
                        <Button
                            className="w-full h-12 font-black gradient-primary shadow-xl rounded-2xl group"
                            onClick={handleCreateTicket}
                            disabled={isCreating}
                        >
                            {isCreating ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                <>
                                    CREAR TICKET DE SOPORTE
                                    <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}

